import type { SSEMessage } from '@/_shared/types';

export interface TurnSlice {
  turnId: string | null;
  startedAt: number | null;
  messages: SSEMessage[];
  status: 'active' | 'completed' | 'errored' | 'aborted' | 'interrupted';
}

export function sliceIntoTurns(messages: SSEMessage[]): TurnSlice[] {
  if (messages.length === 0) return [];

  const slices: TurnSlice[] = [];
  let current: TurnSlice | null = null;

  for (const msg of messages) {
    if (msg.type === 'turn_start') {
      // Close any preamble or prior slice
      if (current) {
        if (current.status === 'active') current.status = 'completed';
        slices.push(current);
      }
      current = {
        turnId: msg.turnId,
        startedAt: msg.startedAt,
        messages: [msg],
        status: 'active',
      };
    } else if (current) {
      current.messages.push(msg);
      if (msg.type === 'error' && current.status === 'active') {
        current.status = 'errored';
      } else if (msg.type === 'turn_complete' && current.status === 'active') {
        current.status = msg.outcome === 'interrupted' ? 'interrupted' : 'completed';
      } else if (msg.type === 'turn_aborted' && current.status === 'active') {
        current.status = 'aborted';
      }
    } else {
      // No turn_start yet — preamble
      current = {
        turnId: null,
        startedAt: null,
        messages: [msg],
        status: 'completed',
      };
    }
  }

  if (current) slices.push(current);
  return slices;
}

/**
 * Remove all events belonging to a specific turn from a messages array.
 * Used to exclude active-turn events from chat.get data when the SSE stream
 * is the single source of truth for that turn.
 */
export function filterOutTurn(messages: SSEMessage[], turnId: string): SSEMessage[] {
  let inTurn = false;
  // Legacy sweepStaleTurns appended both error + turn_complete. Phase 1 failed turns
  // emit a single terminal error (no trailing turn_complete).
  let consumeNextComplete = false;
  return messages.filter((m) => {
    if (m.type === 'turn_start' && m.turnId === turnId) {
      inTurn = true;
      return false;
    }
    if (m.type === 'turn_complete' && m.turnId === turnId && (inTurn || consumeNextComplete)) {
      inTurn = false;
      consumeNextComplete = false;
      return false;
    }
    if (inTurn && m.type === 'error') {
      inTurn = false;
      // Keep consumeNextComplete true only for the legacy error+turn_complete pair.
      // A Phase 1 failed error already carries outcome and is the sole terminal.
      consumeNextComplete = m.outcome !== 'failed';
      return false;
    }
    if (inTurn && m.type === 'turn_aborted') {
      inTurn = false;
      return false;
    }
    // Terminal error for this turn outside an open turn_start window (failed before start).
    if (m.type === 'error' && m.turnId === turnId) {
      return false;
    }
    return !inTurn;
  });
}

/**
 * True when the persisted history already contains a terminal event (turn_complete / error /
 * turn_aborted) for `turnId` — i.e. the turn is finished and fully present in `chat.get`.
 *
 * Used to decide whether to open the per-turn SSE stream. The stream endpoint replays ALL of a
 * turn's persisted events, so connecting to an already-terminal turn (e.g. a stale router
 * `pendingTurnId` after SPA navigate-back) re-delivers rows already in history — which, once the
 * turn is excluded from history by `filterOutTurn`, get appended out of chronological order.
 * Returns false when the turn isn't in history yet (a genuinely new/active turn we should stream).
 */
export function isTurnTerminalInHistory(messages: SSEMessage[], turnId: string): boolean {
  // Queued turns can fail before turn_start. Their terminal event carries the identity itself.
  if (
    messages.some(
      (message) =>
        (message.type === 'error' || message.type === 'turn_complete' || message.type === 'turn_aborted') &&
        message.turnId === turnId,
    )
  ) {
    return true;
  }
  // Legacy error events have no turnId, so infer their turn from the surrounding start event.
  const slice = sliceIntoTurns(messages).find((s) => s.turnId === turnId);
  return slice != null && slice.status !== 'active';
}

/**
 * Drop a trailing turn that never terminated, so history ends at the last completed turn.
 *
 * A viewer normally streams the owner's live turn. This is a fallback for legacy history with an
 * unfinished marker but no durable active turn. Only the last slice can be active (a later
 * `turn_start` closes an earlier one), so this removes at most one turn. Callers use it only when
 * read-only and no stream was opened; with a stream, `filterOutTurn` handles the turn instead.
 */
export function dropUnfinishedTrailingTurn(messages: SSEMessage[]): SSEMessage[] {
  const slices = sliceIntoTurns(messages);
  const last = slices[slices.length - 1];
  if (last?.status !== 'active' || last.turnId === null) return messages;
  return filterOutTurn(messages, last.turnId);
}

/**
 * Merge fetched history with the live SSE buffer for session init, dropping any live message whose
 * `seq` already appears in history. Without this, a completed turn present in BOTH — e.g. it finished
 * before `chat.get` returned, so `knownTurnId` no longer matches it and `filterOutTurn` leaves it in
 * history while the live buffer still holds it — renders twice. Seq-less (optimistic / not-yet-persisted)
 * live messages are always kept; the active turn is already excluded from `history` by `filterOutTurn`.
 */
export function mergeHistoryWithLiveMessages(history: SSEMessage[], live: SSEMessage[]): SSEMessage[] {
  const historySeqs = new Set<number>();
  for (const m of history) {
    if (m.seq != null) historySeqs.add(m.seq);
  }
  const dedupedLive = live.filter((m) => m.seq == null || !historySeqs.has(m.seq));
  return [...history, ...dedupedLive];
}

/**
 * `messages` with `message` placed by its seq: after the last message with a lower seq, so a late
 * message lands with the turn it belongs to, ahead of a newer turn's messages (seq-less optimistic ones
 * included). A message without a seq, or one whose seq the list already holds, is appended or ignored.
 */
export function insertBySeq(messages: SSEMessage[], message: SSEMessage): SSEMessage[] {
  const { seq } = message;
  if (seq == null) return [...messages, message];
  let at = messages.length;
  for (let i = messages.length - 1; i >= 0; i--) {
    const current = messages[i]!.seq;
    if (current == null) continue;
    if (current === seq) return messages;
    if (current < seq) {
      at = i + 1;
      break;
    }
    at = i;
  }
  return [...messages.slice(0, at), message, ...messages.slice(at)];
}

/**
 * Tool_use ids to pre-mark as "already adopted" on session init — every completed turn's tools, with the
 * ACTIVE turn (if any) excluded. Excluding the active turn is load-bearing: on a reload mid-turn its
 * tool_use is already in the reloaded history but its tool_result streams live, so pre-marking it would
 * make `adoptDashboardToolResults` permanently skip that artifact once the result arrives.
 */
export function completedTurnToolUseIds(messages: SSEMessage[], activeTurnId: string | null): Set<string> {
  const completed = activeTurnId ? filterOutTurn(messages, activeTurnId) : messages;
  const ids = new Set<string>();
  for (const m of completed) {
    if (m.type === 'tool_use') ids.add(m.id);
  }
  return ids;
}
